import React, { useState, useEffect } from 'react';
import Select from '../../components/ui/Select';
import EmptyState from '../../components/ui/EmptyState';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import { adminService } from '../../services/adminService';
import ConferenceTable from './components/ConferenceTable';
import ConferenceFilters from './components/ConferenceFilters';

const AdminConferenceManagementInterface = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const { user, userProfile } = useAuth();
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [selectedConferences, setSelectedConferences] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
      return;
    }

    // The profile loads asynchronously. Without this, the guard runs on the
    // first render while userProfile is still null, reads that as "not an
    // admin", and redirects — so admins could never deep-link or refresh
    // an admin page, only reach it by client-side navigation after load.
    if (!userProfile) return;

    if (!(userProfile?.isPlatformAdmin ?? userProfile?.roles?.includes('admin'))) {
      navigate('/user-dashboard');
      return;
    }

    loadConferences();
  }, [user, userProfile, navigate, filters]);

  const loadConferences = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getAllConferences({
        search: filters?.search,
        status: filters?.status
      });
      
      // Apply sorting
      const sorted = [...data]?.sort((a, b) => {
        const aVal = a?.[filters?.sortBy];
        const bVal = b?.[filters?.sortBy];
        if (filters?.sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
      
      setConferences(sorted);
    } catch (err) {
      setError(err?.message || t('conferenceManagement.errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedConferences?.length === 0) {
      toastError(t('conferenceManagement.bulkActions.selectFirst'));
      return;
    }

    if (!window.confirm(t('conferenceManagement.confirmations.bulkStatusUpdate', { count: selectedConferences?.length, status: newStatus }))) {
      return;
    }

    try {
      await Promise.all(
        selectedConferences?.map(id => adminService?.updateConferenceStatus(id, newStatus))
      );
      setSelectedConferences([]);
      loadConferences();
    } catch (err) {
      setError(err?.message || t('conferenceManagement.errors.failedToUpdateBulk'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedConferences?.length === 0) {
      toastError(t('conferenceManagement.bulkActions.selectFirst'));
      return;
    }

    if (!window.confirm(t('conferenceManagement.confirmations.bulkDelete', { count: selectedConferences?.length }))) {
      return;
    }

    try {
      await Promise.all(
        selectedConferences?.map(id => adminService?.deleteConference(id))
      );
      setSelectedConferences([]);
      loadConferences();
    } catch (err) {
      setError(err?.message || t('conferenceManagement.errors.failedToDeleteBulk'));
    }
  };

  const handleSelectAll = () => {
    if (selectedConferences?.length === conferences?.length) {
      setSelectedConferences([]);
    } else {
      setSelectedConferences(conferences?.map(c => c?.id));
    }
  };

  const handleSelectConference = (conferenceId) => {
    if (selectedConferences?.includes(conferenceId)) {
      setSelectedConferences(selectedConferences?.filter(id => id !== conferenceId));
    } else {
      setSelectedConferences([...selectedConferences, conferenceId]);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      
      <div className="page-shell py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t('conferenceManagement.title')}</h1>
              <p className="text-muted-foreground">{t('conferenceManagement.subtitle')}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/master-admin-dashboard')}
            >
              <Icon name="ArrowLeft" size={18} className="mr-2" />
              {t('conferenceManagement.backToDashboard')}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-error/10 border-2 border-error rounded-lg">
            <p className="text-error font-medium">{error}</p>
          </div>
        )}

        {/* Filters */}
        <ConferenceFilters filters={filters} setFilters={setFilters} />

        {/* Bulk Actions */}
        {selectedConferences?.length > 0 && (
          <div className="mb-6 p-4 bg-primary/10 border-2 border-primary rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-foreground font-medium">
                {t('conferenceManagement.bulkActions.selectedCount', { count: selectedConferences?.length })}
              </span>
              <div className="flex items-center gap-2">
                <Select
                  value={undefined}
                  onChange={(value) => handleBulkStatusUpdate(value)}
                  placeholder={t('conferenceManagement.bulkActions.changeStatus')}
                  className="min-w-[13rem]"
                  options={[
                    { value: 'published', label: t('conferenceManagement.status.published') },
                    { value: 'submission_open', label: t('conferenceManagement.status.submissionOpen') },
                    { value: 'submission_closed', label: t('conferenceManagement.status.submissionClosed') },
                    { value: 'cancelled', label: t('conferenceManagement.status.cancelled') },
                  ]}
                />
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Icon name="Trash2" size={16} className="mr-2" />
                  {t('conferenceManagement.bulkActions.deleteSelected')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Conference Table */}
        <div className="bg-card border-2 border-border rounded-lg shadow-elevation-2">
          {loading ? (
            <TableSkeleton rows={8} columns={6} />
          ) : conferences?.length === 0 ? (
            <EmptyState
              icon="FolderOpen"
              title={t('conferenceManagement.emptyState')}
              body={t('conferenceManagement.emptyStateBody', 'Conferences created by organisers appear here. None match the current view.')}
            />
          ) : (
            <ConferenceTable
              conferences={conferences}
              selectedConferences={selectedConferences}
              onSelectAll={handleSelectAll}
              onSelectConference={handleSelectConference}
              onRefresh={loadConferences}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminConferenceManagementInterface;